import { itemsCol } from './array-types';

export const damageZoneProfiles = {
 fire: {texture:'Fire_02',color:'#ff7426'},
 sparkBlaze: {texture:'FabSparkBlazeSustainCandidateL0',color:'#ff7426'},
 cold: {texture:'FabIceHitCloseV2',color:'#a0e4ff'},
 lightning: {texture:'FabLightningBugHit',color:'#aaa2ff'},
 poison: {texture:'FabPoisonBoltHit',color:'#82ce45'},
 acid: {texture:'FabGreenCorrosiveHit',color:'#b0e849'},
 necrotic: {texture:'FabShadowHit',color:'#62b943'},
 radiant: {texture:'FabGoldenHeal',color:'#fff2c4'},
 // Not a damage type: an obscuring cloud (Fog Cloud) — a zone that applies the Obscured status (owner 2026-10-09).
 mist: {texture:'FabVDust',color:'#d8dde2'},
} as const;
export type DamageZoneFamily = keyof typeof damageZoneProfiles;

/** Local sustain intake; unsupported families deliberately have no palette substitute. */
export const zoneSustainProfiles = {
 fire:{meshFx:'MeshFxFireMagicFlame3Sustain',decal:'MeshFxConeDecalRingSpell07',color:'#ff7426'},
 cold:{meshFx:'MeshFxIceMagicSnowstorm1Sustain',decal:'MeshFxConeDecalRingSpell04',color:'#a0e4ff'},
 lightning:{meshFx:'MeshFxLightningMagicAreaSustain',decal:'MeshFxConeDecalUVSChip',color:'#aaa2ff'},
 poison:{meshFx:'MeshFxPosionMagicAreaWaveSustain',decal:'MeshFxConeDecalRingSpell05',color:'#82ce45'},
 acid:{meshFx:'MeshFxPosionMagicAreaWaveSustain',decal:'MeshFxConeDecalNoisecaustic02',color:'#b0e849'},
 necrotic:{meshFx:'MeshFxDarkMagicDOTSustain',decal:'MeshFxConeDecalRingSpell05',color:'#9870bc'},
 radiant:{meshFx:'MeshFxLightMagicAuraSustain',decal:'MeshFxConeDecalRingSpell10',color:'#fff2c4'},
 thunder:{meshFx:'MeshFxAirMagicAuraSustain',decal:'MeshFxConeDecalRingQuarter04',color:'#c6d6e4'},
 air:{meshFx:'MeshFxAirMagicAuraSustain',decal:'MeshFxConeDecalRingSpell08',color:'#c6d6e4'},
 water:{meshFx:'MeshFxWaterMagicArea3Sustain',decal:'MeshFxConeDecalRingWaterWave',color:'#62bddd'},
 earth:{meshFx:'MeshFxEarthMagicAuraSustain',decal:'MeshFxConeDecalGroundCrackRing',color:'#bda378'},
 blood:{meshFx:'MeshFxBloodMagicArea1Sustain',decal:'MeshFxConeDecalRingSpell05',color:'#c13e42'},
} as const;
export type SustainZoneFamily=keyof typeof zoneSustainProfiles;
export type ZoneVisualFamily=DamageZoneFamily|SustainZoneFamily|'force'|'arcane'|'psychic'|'physical'|'nature';
export function sustainFt3dZoneVisual(family:unknown,shape:unknown):Ft3dZoneVisual|undefined {
 if(typeof family!=='string'||!Object.prototype.hasOwnProperty.call(zoneSustainProfiles,family)
   ||!['line','cone','circle','sphere','cylinder','cube','square'].includes(String(shape)))return;
 const p=zoneSustainProfiles[family as SustainZoneFamily];
 return {schema:'ft.vfx.zone.v1',style:'purchased-mesh-sustain',family:family as SustainZoneFamily,color:p.color,meshFx:p.meshFx,decal:p.decal};
}

/** Class-aura stance (2026-09-24): an always-on class aura renders as a sparse ring of motes, gold for an allied aura,
 *  violet for an enemy-only one. Only emitted while the encounter is in combat (persistent-zones.ts). */
export const CLASS_AURA_TWINKLE_COLORS = { ally: '#ffd27a', foe: '#8f6bff' } as const;
export type ClassAuraTwinkleTeam = keyof typeof CLASS_AURA_TWINKLE_COLORS;
export interface Ft3dZoneVisual { schema:'ft.vfx.zone.v1';style:'acid-stream'|'purchased-damage-fill'|'class-aura-twinkle'|'purchased-mesh-sustain';color:string;family?:ZoneVisualFamily;team?:ClassAuraTwinkleTeam;meshFx?:string;decal?:string }
export function parseFt3dZoneVisual(value:unknown,shape:unknown):Ft3dZoneVisual|undefined {
 if(!value||typeof value!=='object')return;
 const v=value as Record<string,unknown>;
 if(v.schema!=='ft.vfx.zone.v1')return;
 if(v.style==='purchased-mesh-sustain'){
  const parsed=sustainFt3dZoneVisual(v.family,shape);
  if(!parsed||v.meshFx!==parsed.meshFx||(v.decal!==undefined&&v.decal!==parsed.decal))return;
  return {...parsed,...(v.decal===undefined?{decal:undefined}:{})};
 }
 if(v.style==='class-aura-twinkle'){
   if(!['circle','sphere','cylinder','cube','square'].includes(String(shape)))return;
   const team=(v.team==='foe'?'foe':'ally') as ClassAuraTwinkleTeam;
   return {schema:'ft.vfx.zone.v1',style:'class-aura-twinkle',team,color:CLASS_AURA_TWINKLE_COLORS[team]};
 }
 if(v.style==='purchased-damage-fill'&&typeof v.family==='string'&&Object.prototype.hasOwnProperty.call(damageZoneProfiles,v.family)
   &&['line','cone','circle','sphere','cylinder','cube','square'].includes(String(shape))
   &&(v.family!=='sparkBlaze'||shape==='cube')){
   const family=v.family as DamageZoneFamily;
   return {schema:'ft.vfx.zone.v1',style:v.style,family,color:damageZoneProfiles[family].color};
 }
 if(shape!=='line'||v.style!=='acid-stream'||typeof v.color!=='string'||!/^#[0-9a-f]{6}$/i.test(v.color))return;
 return {schema:'ft.vfx.zone.v1',style:'acid-stream',color:v.color};
}
/** Owner-authorized provisional pAOE fills (2026-09-21), separate from cast recipes. */
export function damageFt3dZoneVisual(damageType:unknown):Ft3dZoneVisual|undefined {
 const families:Record<string,ZoneVisualFamily>={
  'A8469481-849E-4BBA-986E-7C418DCA06A1':'fire','8C455214-9F98-43C7-B9BC-1C74EE60805D':'cold',
  '944EC0D7-3320-42B1-B28D-68980DAA9719':'lightning','4EBFF986-A4AA-4709-BE68-E97D29B66D18':'poison',
  'F66879A9-5BDC-4996-8419-AF162CFCDC5F':'acid','D66B715F-E086-4C4B-A887-4BDB38AE32EB':'necrotic',
  '5850D66F-70E8-467D-9A5C-93464A5A12C4':'radiant',
  'F79CF36E-F439-44C3-9668-050683A30F1C':'thunder','25F9052F-B963-43CB-991E-EB315E08A549':'force',
  'B61FDA44-4707-4539-B39B-54FA1FCBAA71':'psychic','0C528CF8-1B5A-453C-9BDE-73CBBC239A2A':'physical',
  'C62E5227-0AB4-4C34-9058-DE97E36B0706':'physical','422B9C6D-9558-47CD-AC21-1FB3B45EF33E':'physical',
  'FIRE':'fire','COLD':'cold','LIGHTNING':'lightning','POISON':'poison','ACID':'acid','NECROTIC':'necrotic','RADIANT':'radiant',
  'THUNDER':'thunder','FORCE':'force','ARCANE':'arcane','PSYCHIC':'psychic','PHYSICAL':'physical','BLOOD':'blood',
  'NATURE':'nature','PLANT':'nature','WATER':'water','EARTH':'earth','AIR':'air','MIST':'mist','FOG':'mist',
 };
 const family=typeof damageType==='string'?families[damageType.toUpperCase()]:undefined;
 if(!family)return;
 const sustain=sustainFt3dZoneVisual(family,'sphere');if(sustain)return sustain;
 if(family==='mist')return obscuringFt3dZoneVisual('sphere');
 return undefined; // Force, psychic, physical and living plants await supported owned art.
}
/** The Obscured status (perception.ts PERCEPTION_STATUS.obscured): a zone applying it is a cloud you cannot see through. */
export const OBSCURED_STATUS_ID = 'E2054884-038E-3D6E-56C5-19ABBFED8379';
/** An obscuring zone with no damage art of its own reads as drifting mist (Fog Cloud). */
export function obscuringFt3dZoneVisual(shape:unknown):Ft3dZoneVisual|undefined {
 return parseFt3dZoneVisual({schema:'ft.vfx.zone.v1',style:'purchased-damage-fill',family:'mist'},shape);
}
/** Compiled published assets only; this does not look up spells or infer art from damage types. */
export function compiledFt3dZoneVisual(definition:Record<string,unknown>,shape:unknown):Ft3dZoneVisual|undefined {
 const assets=definition.assets as Record<string,unknown>|undefined;
 const r=assets?.magic_vfx as Record<string,unknown>|undefined;
 if(r?.schema!=='ft.vfx.recipe.v1'||r.kind!=='area'||r.lifecycle!=='zone')return;
 if(r.zoneSustain){const z=r.zoneSustain as Record<string,unknown>;return parseFt3dZoneVisual({...z,schema:'ft.vfx.zone.v1',style:'purchased-mesh-sustain'},shape);}
 if(shape==='cube'&&r.areaStyle==='purchased-spark-ignition'&&r.texture==='FabSparkBlazeIgnitionL0')
  return parseFt3dZoneVisual({schema:'ft.vfx.zone.v1',style:'purchased-damage-fill',family:'sparkBlaze'},shape);
 return parseFt3dZoneVisual({schema:'ft.vfx.zone.v1',style:r.areaStyle,color:r.color},shape);
}

/** Both surfaces call this exact precedence. Cold obscuring weather stays mist; Sleet Storm does not
 * become a field of ice impacts. Explicit compiled zone art remains authoritative. */
export function resolveFt3dZoneVisual(definition:Record<string,unknown>,shape:unknown,damageType:unknown,obscures:boolean):Ft3dZoneVisual|undefined {
 const items=definition.items as Record<string,unknown>|undefined;
 const id=String(items?.item_id??definition.item_id??'').toUpperCase();
 // Owner-named weather art remains mist even when a catalog snapshot lacks an Obscured layer.
 // This does not create a zone or change persistent_aoe.
 if(['CED0CFCC-937C-DD0B-1ED2-8B2874AB1887','2582663A-C333-A5AF-9004-1879F459BB26'].includes(id))return obscuringFt3dZoneVisual(shape);
 const compiled=compiledFt3dZoneVisual(definition,shape);if(compiled)return compiled;
 const damage=damageFt3dZoneVisual(damageType);
 if(obscures&&(!damage||damage.family==='cold'||damage.family==='water'))return obscuringFt3dZoneVisual(shape);
 return damage??(obscures?obscuringFt3dZoneVisual(shape):undefined);
}

/** Painted ribbons use owned sustains only. Ice already has bound section art; Water's
 * exact catalog identity supplies its element without guessing from its absent damage type. */
export function resolveFt3dPaintedWallVisual(definition:Record<string,unknown>,damageType:unknown):Ft3dZoneVisual|undefined {
 const id=String(itemsCol(definition,'item_id')??'').toUpperCase();
 if(id==='98E982F2-46EF-1B81-87D2-6B0E8AFDE922')return;
 const visual=compiledFt3dZoneVisual(definition,'line')??damageFt3dZoneVisual(damageType)
   ??(id==='3375068E-F4FB-C567-C7D4-D2D4B861CD10'?sustainFt3dZoneVisual('water','line'):undefined);
 return visual?.style==='purchased-mesh-sustain'?visual:undefined;
}
